import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useVisibility } from '../hooks/useVisibility';
import { useI18n } from '../i18n';
import type { I18n } from '../i18n';
import { clockTime, freshness, timeAgo } from '../lib/format';
import { Module, ModuleHeader } from './ui/Module';

/**
 * The most important thing on the screen (SPEC §11).
 *
 * **Android has no equivalent, and does not need one.** It runs in a foreground
 * service, survives reboot via `BootReceiver`, and a device that stops
 * reporting there is a bug somebody notices from the backend. This app dies with
 * the tab, does not come back after a restart, and only measures while the
 * screen is on — so "this device has been silent for six hours" is the *normal*
 * consequence of a locked phone rather than a fault, and the person who happens
 * to pick the handset up is the entire monitoring system. They have to see it in
 * the first second, without opening a dashboard somewhere else.
 *
 * So this is an addition to the Android layout, not a port of one — but it is
 * built out of the same pieces (`Module` + `ModuleHeader`) so it reads as part
 * of the same app rather than as a web widget bolted onto it. The only thing it
 * adds to the module idiom is a 4px freshness stripe down the left edge, which
 * is what lets the state be read from across a desk without focusing on the
 * words.
 *
 * `freshness()` in `src/lib/format.ts` stays the single source of truth for that
 * state: this component turns its answer into `module--${state}` and never
 * decides a colour itself. The band thresholds are a data question (one missed
 * cycle is a blip, two is a stopped device), and they are tested there. The same
 * goes for the age above them — `timeAgo()` decides which band the number falls
 * in, this file only chooses the words for it.
 */
export function LastCheckBanner({
  lastSuccessAt,
  intervalMinutes,
  nextRunAt,
  running,
  active,
}: {
  lastSuccessAt: number | null;
  intervalMinutes: number;
  nextRunAt: number | null;
  running: boolean;
  /**
   * Whether the pane holding this banner is the one on screen.
   *
   * The shell keeps all three tabs mounted, and the document itself can be
   * hidden while the phone is locked or Safari is in another tab — so without
   * this, the clock below ticks once a second forever, for a countdown behind
   * two layers of nothing.
   */
  active: boolean;
}): JSX.Element {
  const { t, n, locale, seconds } = useI18n();
  const visible = useVisibility();

  // A countdown has to tick or it is a screenshot of a countdown. Once a second
  // while there is something counting down, once every half minute otherwise —
  // the "reported 5 min ago" line only needs to keep up with the minute hand,
  // and this component re-renders nothing but its own six text nodes.
  //
  // `null` while nobody can see it. `nextRunAt` is non-null from the first
  // scheduled cycle onward, so the 30s case is all but unreachable in practice
  // and the real period is 1s for the life of the app — a timer that would
  // otherwise keep firing behind the Settings tab and behind a locked screen.
  const now = useNow(active && visible ? (running || nextRunAt !== null ? 1_000 : 30_000) : null);

  const state = freshness(lastSuccessAt, intervalMinutes, now);

  /**
   * Memoised on the timestamp, not recomputed on every tick.
   *
   * `lastSuccessAt` changes once per cycle — minutes apart — while this
   * component re-renders once a second, so formatting it inline meant an
   * `Intl.DateTimeFormat` call per second to produce the same string it
   * produced the second before. The locale is a dependency because the clock is
   * formatted for the language on screen, and that can change under it.
   */
  const at = useMemo(
    () => (lastSuccessAt === null ? null : clockTime(lastSuccessAt, locale.tag)),
    [lastSuccessAt, locale.tag],
  );

  return (
    <Module className={`module--${state}`}>
      <ModuleHeader text={t('ios_last_check_label')} />
      <div className="last-check__body">
        {/*
         * The live region covers the verdict and its timestamp but deliberately
         * **not** the countdown below it. A polite region wrapping a per-second
         * counter makes VoiceOver re-read the whole banner every second, which
         * is worse than announcing nothing at all.
         */}
        <div aria-live="polite">
          <p className="last-check__value t-headline-small">
            {lastSuccessAt === null
              ? t('ios_last_check_never')
              : t('ios_last_check_ago', agoText(t, n, lastSuccessAt, now))}
          </p>
          {at === null ? null : (
            <p className="last-check__sub t-body-small">
              {/*
               * Wall clock, not another relative phrase: "at 14:32:07" is what an
               * operator compares against the backend's own log, and it is the
               * one line on this module that does not move while they read it.
               */}
              {t('ios_last_check_at', at)}
            </p>
          )}
          {state === 'stale' ? (
            <p className="last-check__warn t-body-medium">
              {/*
               * `freshness` calls it stale past two missed periods, so the number
               * quoted here has to be that same two periods or the sentence
               * contradicts the stripe beside it.
               */}
              {t('ios_last_check_stale', n(Math.round(intervalMinutes * 2)))}
            </p>
          ) : null}
        </div>

        <p className="last-check__next t-body-medium">
          {running
            ? t('ios_running_now')
            : nextRunAt === null
              ? t('ios_next_run_none')
              : /*
                 * A clock, not a sentence. `I18n['seconds']` is the port of
                 * Android's `formatSeconds` and `home_next_round` already prints
                 * a wait that way, so the two countdowns on this screen are
                 * spelled the same — and no plural-sensitive string key has to
                 * be invented for a number that changes every second. The floor
                 * at zero is what keeps a late tick from rendering `0:-1`.
                 */
                t('ios_next_run', seconds(Math.round(Math.max(0, nextRunAt - now) / 1000)))}
        </p>
      </div>
    </Module>
  );
}

/**
 * A clock that re-renders this component and nothing else.
 *
 * Deliberately local rather than a `src/hooks/` export: HomeScreen re-renders on
 * every probe result already, and hanging a second timer off the screen root
 * would re-render the whole dashboard — several hundred result rows on a large
 * link list — once a second for the sake of one countdown.
 *
 * A `null` period means **no interval at all**, which is the state this hook
 * spends most of its life in on a real device: the pane is behind another tab,
 * or the screen is off. There is nothing to keep up with while nobody is
 * looking, and on a phone the app cannot recharge, a timer nobody reads is the
 * cheapest thing in the app to stop.
 */
function useNow(periodMs: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (periodMs === null) return;
    // Resync before arming, because the wall clock kept moving while this was
    // suspended. Without it a banner that comes back after an hour behind the
    // Settings tab shows the age it was frozen at until the first tick — which
    // is the one lie this module exists to prevent.
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), periodMs);
    return () => clearInterval(id);
  }, [periodMs]);
  return now;
}

/**
 * `"Reported 5 min ago"` in the active language.
 *
 * The bands live in `timeAgo()` in `src/lib/format.ts` — including the
 * `Math.max(0, …)` that keeps a phone whose clock jumped from reporting a
 * negative age — and only the *words* are chosen here. That split is the whole
 * point: this used to be a second copy of those boundaries written inline, with
 * a comment asking the next person to keep the two in step by hand, because
 * `timeAgo` returned hard-coded Vietnamese. The tested copy had no callers and
 * the live copy had no tests. `format.ts` is locale-neutral now, so there is one
 * copy of the thresholds and it is the one under test.
 *
 * `src/lib` still never returns a sentence: `t` lives behind a React context,
 * and pulling it into `format.ts` would cost the node test environment that
 * makes those bands testable without a DOM.
 */
function agoText(t: I18n['t'], n: I18n['n'], from: number, now: number): string {
  const ago = timeAgo(from, now);
  switch (ago.unit) {
    case 'just-now':
      return t('ios_ago_just_now');
    case 'seconds':
      return t('ios_ago_seconds', n(ago.value));
    case 'minutes':
      return t('ios_ago_minutes', n(ago.value));
    // `rest` is why these two carry a second argument: with an hourly interval,
    // "2 h 5 min ago" and "2 h ago" are different stories about the same device.
    case 'hours':
      return t('ios_ago_hours', n(ago.value), n(ago.rest));
    case 'days':
      return t('ios_ago_days', n(ago.value), n(ago.rest));
  }
}
